import React, { useEffect, useReducer, useRef } from 'react';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { convertNumberInput } from '../../../utils/validations';
import { stiffnessConversionReducer, stiffnessConversionInitialState, StiffnessConversionState } from './reducer';
import { useConversion } from '../../ConversionContext';
import { getConvertedUnit } from '../../../../components/bearing-search/api/api';
import { ConversionUnitsPayload } from '../../../../components/bearing-search/types/types';
import { ConversionToolTabProps } from '../../types';
import { mapConversionAnalyicsData } from '../../Utils/AnalyticsUtils';
import { tabInputDebounceFunction } from '../Utils/TabUtils';
import { trackScientificNotationChangeEvent } from "../../../utils/analytics";

const TabStiffness: React.FC<ConversionToolTabProps> = ({ description }) => {
	const { exponent, precision } = useConversion();

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const lastInputRef = useRef<{
		category: 'linear' | 'angular';
		unit: string;
		value: string;
	} | null>(null);
	const lastResetCategoryRef = useRef<'linear' | 'angular' | null>(null);

	// Reducer
	const [ stiffnessConversionState, dispatch ] = useReducer(
		stiffnessConversionReducer,
		stiffnessConversionInitialState,
	);
	const currentStateRef = useRef();

	/**
	 * Recalculate all conversions units
	 */
	const recalculateAllConversions = async () => {
		let changedCategories = [];

		for (const [ categoryKey, units ] of Object.entries(stiffnessConversionState)) {
			const category = categoryKey as keyof StiffnessConversionState;

			for (const [ unit, value ] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					if (!changedCategories.includes(category)){
						changedCategories.push(category);
					}
					if (!changedCategories.includes('precision exponent') && exponent) {
						changedCategories.push('precision exponent');
					}
					if (!changedCategories.includes('precision') && precision != 4) {
						changedCategories.push('precision');
					}
					callAndDispatchConversion(category, unit, value, true);
				}
			}
		}

		return changedCategories;
	};

	/**
	 * Reset all conversions units
	 */
	const resetAllConversions = () => {
		for (const [ categoryKey, units ] of Object.entries(stiffnessConversionState)) {
			const category = categoryKey as keyof StiffnessConversionState;

			for (const [ value ] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					dispatch({
						type: 'reset_category',
						payload: {
							category,
						},
					});
				}
			}
		}
	};

	/**
	 * Update the converted values with the new exponent
	 * convert based on the first input of each category
	 */
	useEffect(() => {
		currentStateRef.current = stiffnessConversionState;
		recalculateAllConversions().then((result) => {
			trackScientificNotationChangeEvent('conversionTool', result);
		});
	}, [ exponent ]);

	/**
	 * Reset the values when precision is changed
	 */
	useEffect(() => {
		resetAllConversions();
	}, [ precision ]);

	// Map of conversion types for API payload
	const conversionTypeMap: Record<string, string[]> = {
		npm: [ 'newtonPerMeterToPoundForcePerInch', 'newtonPerMetertoNewtonPerMillimeter' ],
		lbfpin: [ 'poundForcePerInchToNewtonPerMeter', 'poundForcePerInchToNewtonPerMillimeter' ],
		npmm: [ 'newtonPerMillimeterToNewtonPerMeter', 'newtonPerMillimeterTopoundForcePerInch' ],
		nmprad: [ 'newtonMeterPerRadianTopoundForceInchPerRadian' ],
		lbfinprad: [ 'poundForceInchPerRadianToNewtonMeterPerRadian' ],
	};

	// Map of converted units from API response
	const convertedUnitMap: Record<string, string> = {
		newtonPerMeterToPoundForcePerInch: 'lbfpin',
		newtonPerMetertoNewtonPerMillimeter: 'npmm',
		poundForcePerInchToNewtonPerMeter: 'npm',
		poundForcePerInchToNewtonPerMillimeter: 'npmm',
		newtonPerMillimeterToNewtonPerMeter: 'npm',
		newtonPerMillimeterTopoundForcePerInch: 'lbfpin',
		newtonMeterPerRadianTopoundForceInchPerRadian: 'lbfinprad',
		poundForceInchPerRadianToNewtonMeterPerRadian: 'nmprad',
	};

	/**
	 * @param {('linear' | 'angular')} category - The category of the conversion.
	 * @param {string}                 unit     - The unit to be converted.
	 * @param {string}                 value    - The value to be converted.
	 * @param {boolean}                skipAnalyticCall - If Precision or Scientific is changed, skip analytic call
	 */
	const callAndDispatchConversion = async (
		category: 'linear' | 'angular',
		unit: string,
		value: string,
		skipAnalyticCall: boolean
	) => {
		const conversionTypeArray = conversionTypeMap[ unit ] || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		const conversionPayload: ConversionUnitsPayload = {
			unitConversionInputs: [
				{
					conversionType: conversionTypeArray,
					inputValue: value,
					precision,
					exponent,
				},
			],
		};

		try {
			const response = await getConvertedUnit(conversionPayload);
			// ANALYTICS
			if (!skipAnalyticCall) {
				mapConversionAnalyicsData(category, conversionPayload, response);
			}

			if (response.unitConversionResults && category !== lastResetCategoryRef.current) {
				for (const conversionResult of response.unitConversionResults) {
					const convertedType = conversionResult.convertedTypes;
					const convertedValue = conversionResult.convertedValue;
					const convertedUnit = convertedUnitMap[ convertedType ];

					if (! convertedUnit) {
						console.log(`${ convertedType } index doesn't exit in convertedUnitMap`);
						return;
					}

					const converted = {
						[ convertedUnit ]: convertedValue.replaceAll(/,/g, ''),
					};

					dispatch({
						type: 'update_unit',
						payload: {
							category,
							updatedUnits: converted,
						},
					});
				}
			} else {
				dispatch({
					type: 'reset_category',
					payload: {
						category,
					},
				});
			}
		} catch (error) {
			console.error('Conversion API failed', error);
		}
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 */
	const handleInputChange = (
		category: 'linear' | 'angular',
		unit: string,
		value: string,
	) => {
		const numericValue = convertNumberInput(value);

		if (numericValue === undefined) {
			dispatch({
				type: 'reset_category',
				payload: { category },
			});

			lastResetCategoryRef.current = category;

			return; // return, don't call API
		}

		dispatch({
			type: 'update_unit',
			payload: {
				category,
				updatedUnits: { [ unit ]: value },
			},
		});

		lastResetCategoryRef.current = null;
		lastInputRef.current = { category, unit, value };

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);

		debounceTimeout.current = setTimeout(() => {
			tabInputDebounceFunction(lastInputRef, callAndDispatchConversion);
		}, debounceDelay);
	};

	/**
	 * @param currentValue
	 * @param category
	 * @param unit
	 * @param value
	 */
	const handleBlur = (
		currentValue: string,
		category: 'linear' | 'angular',
		unit: string,
		newValue: string,
	) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = null;
		}

		const numericValue = Number.parseFloat(newValue);
		if (Number.isNaN(numericValue) || numericValue === 0) {
			return;
		}

		if (currentValue == newValue) {
			return;
		}

		callAndDispatchConversion(category, unit, newValue, false);
	};

	return (
		<>
			{/* Degree */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Linear</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-linear-npm"
								label={''}
								unitLabel={'N/m'}
								value={stiffnessConversionState.linear.npm}
								min={0}
								onChange={(e) => handleInputChange('linear', 'npm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'npm', stiffnessConversionState.linear.npm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-linear-lbfpin"
								label={''}
								unitLabel={'lbf/in'}
								value={stiffnessConversionState.linear.lbfpin}
								min={0}
								onChange={(e) => handleInputChange('linear', 'lbfpin', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'lbfpin', stiffnessConversionState.linear.lbfpin ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-linear-npmm"
								label={''}
								unitLabel={'N/mm'}
								value={stiffnessConversionState.linear.npmm}
								min={0}
								onChange={(e) => handleInputChange('linear', 'npmm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'npmm', stiffnessConversionState.linear.npmm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Angular */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Angular</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-angular-nmprad"
								label={''}
								unitLabel={'N.m/rad'}
								value={stiffnessConversionState.angular.nmprad}
								min={0}
								onChange={(e) => handleInputChange('angular', 'nmprad', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angular', 'nmprad', stiffnessConversionState.angular.nmprad ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-angular-lbfinprad"
								label={''}
								unitLabel={'lbf.in/rad'}
								value={stiffnessConversionState.angular.lbfinprad}
								min={0}
								onChange={(e) => handleInputChange('angular', 'lbfinprad', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'angular', 'lbfinprad', stiffnessConversionState.angular.lbfinprad ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TabStiffness;
